export class StringDevViewportLayer {
  private screenRoot: HTMLDivElement | null = null;
  private world: HTMLDivElement | null = null;
  private screen: HTMLDivElement | null = null;
  private worldHost: HTMLElement | null = null;
  private hostPositionWasPatched = false;
  private hostPositionInlineValue: string | null = null;

  constructor(
    private readonly id: string,
    private readonly zIndex: number,
  ) {}

  public ensure(host?: HTMLElement | null): HTMLDivElement {
    if (this.screenRoot?.isConnected) {
      if (host) {
        this.attachWorldToHost(host);
      }
      return this.screenRoot;
    }

    const existing = document.querySelector<HTMLDivElement>(
      `[data-string-dev-viewport-layer="${this.id}"]`,
    );

    if (existing) {
      this.screenRoot = existing;
      this.screen = existing.querySelector<HTMLDivElement>(
        `[data-string-dev-viewport-screen="${this.id}"]`,
      );
      if (host) {
        this.attachWorldToHost(host);
      }
      return existing;
    }

    const root = document.createElement("div");
    root.setAttribute("data-string-dev-viewport-layer", this.id);
    root.setAttribute("data-string-devtools-theme", "");
    root.style.position = "fixed";
    root.style.inset = "0";
    root.style.zIndex = String(this.zIndex);
    root.style.pointerEvents = "none";
    root.style.overflow = "hidden";

    const screen = document.createElement("div");
    screen.setAttribute("data-string-dev-viewport-screen", this.id);
    screen.style.position = "absolute";
    screen.style.inset = "0";
    screen.style.pointerEvents = "none";
    screen.style.overflow = "hidden";

    root.appendChild(screen);

    (document.body ?? document.documentElement).appendChild(root);
    this.screenRoot = root;
    this.screen = screen;
    this.attachWorldToHost(host ?? (document.body ?? document.documentElement));
    return root;
  }

  public getElement(): HTMLDivElement | null {
    return this.screenRoot?.isConnected ? this.screenRoot : null;
  }

  public getWorldElement(host?: HTMLElement | null): HTMLDivElement {
    this.ensure(host);
    return this.world!;
  }

  public getScreenElement(): HTMLDivElement {
    this.ensure();
    return this.screen!;
  }

  public destroy(): void {
    this.restoreHostPosition();
    this.screenRoot?.remove();
    this.screenRoot = null;
    this.world?.remove();
    this.world = null;
    this.screen = null;
    this.worldHost = null;
  }

  private attachWorldToHost(host: HTMLElement): void {
    if (this.worldHost === host && this.world?.isConnected) {
      return;
    }

    this.restoreHostPosition();
    this.worldHost = host;

    if (!this.world) {
      const world = document.createElement("div");
      world.setAttribute("data-string-dev-viewport-world", this.id);
      world.setAttribute("data-string-devtools-theme", "");
      world.style.position = "absolute";
      world.style.top = "0";
      world.style.left = "0";
      world.style.width = "1px";
      world.style.height = "1px";
      world.style.pointerEvents = "none";
      world.style.overflow = "visible";
      world.style.zIndex = String(this.zIndex);
      this.world = world;
    }

    const shouldPatchHostPosition =
      host !== document.body &&
      host !== document.documentElement &&
      window.getComputedStyle(host).position === "static";

    if (shouldPatchHostPosition) {
      this.hostPositionWasPatched = true;
      this.hostPositionInlineValue = host.style.position || null;
      host.style.position = "relative";
    }

    host.appendChild(this.world);
  }

  private restoreHostPosition(): void {
    if (this.world) {
      this.world.remove();
    }

    if (this.worldHost && this.hostPositionWasPatched) {
      if (this.hostPositionInlineValue == null || this.hostPositionInlineValue === "") {
        this.worldHost.style.removeProperty("position");
      } else {
        this.worldHost.style.position = this.hostPositionInlineValue;
      }
    }

    this.hostPositionWasPatched = false;
    this.hostPositionInlineValue = null;
  }
}
